import WeatherHealthBadge from "../WeatherHealthBadge";
import { formatDateTime } from "../../../users/adminUserFormatters";
import {
  formatFrameAge,
  PLANNED_WEATHER_RADAR_SOURCES,
  UNMONITORED_WEATHER_SOURCES,
} from "../../../../../../utils/radarHealthAdminUtils";

function formatTimestamp(value) {
  if (!value) {
    return "—";
  }

  return formatDateTime(value);
}

function RadarSourceRow({
  row,
  onOpenFrame,
  onCopyDiagnostics,
  onViewPlan,
  onCopyContact,
  onViewApprovalDoc,
  onSiataDiscover,
  onSiataSync,
  onSiataCleanup,
  onAerocivilDiscover,
  onAerocivilSync,
  canManage = false,
}) {
  return (
    <tr>
      <td>
        <strong>{row.source}</strong>
        {row.sourceKey ? (
          <span className="admin-data-health-table__source-key">{row.sourceKey}</span>
        ) : null}
        {row.coverage ? (
          <span className="admin-data-health-table__source-key">{row.coverage}</span>
        ) : null}
        {row.fallbackRole ? (
          <span className="admin-data-health-table__source-key">{row.fallbackRole}</span>
        ) : null}
      </td>
      <td>{row.type}</td>
      <td>
        {row.status ? <WeatherHealthBadge status={row.status} /> : <span>Not monitored yet</span>}
      </td>
      <td>{formatTimestamp(row.latestData)}</td>
      <td>{row.age != null ? formatFrameAge(row.age) : "—"}</td>
      <td>{formatTimestamp(row.lastSync)}</td>
      <td>{row.frames != null ? row.frames : "—"}</td>
      <td className="weather-radar-health-table__error">{row.lastError || "—"}</td>
      <td>
        {row.actions?.length ? (
          <div className="weather-radar-health-table__actions">
            {row.actions.includes("open-frame") && row.frameUrl ? (
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                onClick={() => onOpenFrame(row.frameUrl)}
              >
                Open frame
              </button>
            ) : null}
            {row.actions.includes("copy") ? (
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                onClick={() => onCopyDiagnostics(row.diagnosticsKey)}
              >
                Copy
              </button>
            ) : null}
            {row.actions.includes("view-plan") ? (
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                onClick={() => onViewPlan(row)}
              >
                View plan
              </button>
            ) : null}
            {row.actions.includes("copy-contact") ? (
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                onClick={() => onCopyContact(row)}
              >
                Copy contact request
              </button>
            ) : null}
            {row.actions.includes("view-approval-doc") ? (
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                onClick={() => onViewApprovalDoc(row)}
              >
                View approval doc
              </button>
            ) : null}
            {canManage && row.actions.includes("siata-manage") ? (
              <>
                <button
                  type="button"
                  className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                  onClick={() => onSiataDiscover?.()}
                >
                  Discover
                </button>
                <button
                  type="button"
                  className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                  onClick={() => onSiataSync?.()}
                >
                  Sync
                </button>
                <button
                  type="button"
                  className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                  onClick={() => onSiataCleanup?.()}
                >
                  Cleanup
                </button>
              </>
            ) : null}
            {canManage && row.actions.includes("aerocivil-manage") ? (
              <>
                <button
                  type="button"
                  className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                  onClick={() => onAerocivilDiscover?.()}
                >
                  Discover
                </button>
                <button
                  type="button"
                  className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                  onClick={() => onAerocivilSync?.()}
                >
                  Sync
                </button>
              </>
            ) : null}
          </div>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

const WeatherRadarHealthTable = ({
  health,
  canManage = false,
  onOpenFrame,
  onCopyDiagnostics,
  onViewPlan,
  onCopyContact,
  onViewApprovalDoc,
  onSiataDiscover,
  onSiataSync,
  onSiataCleanup,
  onAerocivilDiscover,
  onAerocivilSync,
}) => {
  const bart = health?.sources?.ideamBart;
  const rainViewer = health?.sources?.rainViewer;
  const siataSantaElena = health?.sources?.siataSantaElena;
  const aerocivilAviation = health?.sources?.aerocivilAviation;
  const legacyOspa = health?.sources?.legacyOspa;
  const siataLive = Boolean(siataSantaElena);
  const aerocivilLive = Boolean(aerocivilAviation);

  const radarRows = [
    {
      id: "ideam_bart",
      source: bart?.sourceLabel || "IDEAM Imagen Compuesta",
      sourceKey: bart?.source,
      type: "Official radar",
      status: bart?.status,
      latestData: bart?.latestRenderableFrameTime || bart?.latestDiscoveredFrameTime,
      age: bart?.latestFrameAgeMinutes,
      lastSync: bart?.lastSyncAt || bart?.lastDiscoveryAt || bart?.checkedAt,
      frames: bart?.renderableFrameCount,
      lastError: bart?.lastError,
      frameUrl: bart?.latestFramePublicUrl,
      diagnosticsKey: "ideamBart",
      actions: ["open-frame", "copy"],
    },
    {
      id: "rainviewer",
      source: rainViewer?.sourceLabel || "RainViewer backup",
      sourceKey: rainViewer?.source,
      type: "Backup radar",
      status: rainViewer?.status,
      latestData: rainViewer?.lastObservedDataAt,
      age: null,
      lastSync: rainViewer?.checkedAt,
      frames: rainViewer?.frameCount,
      lastError: rainViewer?.lastError,
      diagnosticsKey: "rainViewer",
      actions: ["copy"],
    },
    ...(siataLive
      ? [
          {
            id: "siata_santa_elena",
            source: siataSantaElena.sourceLabel || "SIATA Santa Elena Radar",
            sourceKey: siataSantaElena.source || siataSantaElena.sourceKey,
            type: "Local official radar",
            coverage: "Aburrá Valley",
            status: siataSantaElena.status,
            latestData:
              siataSantaElena.latestRenderableFrameTime ||
              siataSantaElena.latestCachedFrameTime ||
              siataSantaElena.latestDiscoveredFrameTime,
            age: siataSantaElena.latestFrameAgeMinutes,
            lastSync: siataSantaElena.lastSyncAt || siataSantaElena.lastSuccessfulSyncAt,
            frames: siataSantaElena.renderableFrameCount ?? siataSantaElena.frameCount,
            lastError: siataSantaElena.lastError,
            frameUrl: siataSantaElena.cache?.latestPublicUrl || null,
            diagnosticsKey: "siataSantaElena",
            fallbackRole: "Preferred local source in Valley Preferred mode",
            actions: ["open-frame", "copy", "siata-manage"],
          },
        ]
      : []),
    ...(aerocivilLive
      ? [
          {
            id: "aerocivil_aviation",
            source: aerocivilAviation.sourceLabel || "Aerocivil Aviation Radar",
            sourceKey: aerocivilAviation.sourceKey,
            type: "Official aeronautical radar",
            coverage: aerocivilAviation.site ? `Site: ${String(aerocivilAviation.site).toUpperCase()}` : null,
            status: aerocivilAviation.status,
            latestData: aerocivilAviation.latestFrameTime,
            age: aerocivilAviation.ageMinutes,
            lastSync: aerocivilAviation.lastSyncAt || aerocivilAviation.checkedAt,
            frames: aerocivilAviation.renderableFrameCount,
            lastError: aerocivilAviation.status === "down" ? aerocivilAviation.message : null,
            frameUrl: aerocivilAviation.latestFrame?.publicUrl || null,
            diagnosticsKey: "aerocivilAviation",
            fallbackRole: "Independent layer — not Valley Preferred",
            actions: ["open-frame", "copy", "aerocivil-manage"],
          },
        ]
      : []),
  ];

  if (legacyOspa) {
    radarRows.push({
      id: "legacy_ospa",
      source: legacyOspa.sourceLabel || "Legacy IDEAM OSPA",
      sourceKey: legacyOspa.source,
      type: "Legacy diagnostic",
      status: legacyOspa.status,
      latestData: legacyOspa.checkedAt,
      age: null,
      lastSync: legacyOspa.checkedAt,
      frames: "—",
      lastError: legacyOspa.lastError,
      diagnosticsKey: "legacyOspa",
      actions: ["copy"],
    });
  }

  const plannedRows = (siataLive ? [] : PLANNED_WEATHER_RADAR_SOURCES.filter((row) => row.id !== "siata_santa_elena"))
    .filter((row) => !(aerocivilLive && row.id === "aerocivil_aviation_radar"))
    .map((row) => ({
    ...row,
    latestData: null,
    age: null,
    lastSync: null,
    frames: "—",
    lastError: row.message,
    diagnosticsKey: null,
  }));

  const unmonitoredRows = UNMONITORED_WEATHER_SOURCES.map((row) => ({
    ...row,
    status: null,
    latestData: null,
    age: null,
    lastSync: null,
    frames: null,
    lastError: null,
    actions: [],
  }));

  return (
    <div className="admin-data-health-table-wrap">
      <table className="admin-data-health-table weather-radar-health-table">
        <thead>
          <tr>
            <th scope="col">Source</th>
            <th scope="col">Type</th>
            <th scope="col">Status</th>
            <th scope="col">Latest data</th>
            <th scope="col">Age</th>
            <th scope="col">Last successful sync/check</th>
            <th scope="col">Frames/items</th>
            <th scope="col">Last error</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {radarRows.map((row) => (
            <RadarSourceRow
              key={row.id}
              row={row}
              canManage={canManage}
              onOpenFrame={onOpenFrame}
              onCopyDiagnostics={onCopyDiagnostics}
              onViewPlan={onViewPlan}
              onCopyContact={onCopyContact}
              onViewApprovalDoc={onViewApprovalDoc}
              onSiataDiscover={onSiataDiscover}
              onSiataSync={onSiataSync}
              onSiataCleanup={onSiataCleanup}
              onAerocivilDiscover={onAerocivilDiscover}
              onAerocivilSync={onAerocivilSync}
            />
          ))}
          {plannedRows.map((row) => (
            <RadarSourceRow
              key={row.id}
              row={row}
              onOpenFrame={onOpenFrame}
              onCopyDiagnostics={onCopyDiagnostics}
              onViewPlan={onViewPlan}
              onCopyContact={onCopyContact}
              onViewApprovalDoc={onViewApprovalDoc}
            />
          ))}
          {unmonitoredRows.map((row) => (
            <RadarSourceRow
              key={row.id}
              row={row}
              onOpenFrame={onOpenFrame}
              onCopyDiagnostics={onCopyDiagnostics}
              onViewPlan={onViewPlan}
              onCopyContact={onCopyContact}
              onViewApprovalDoc={onViewApprovalDoc}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WeatherRadarHealthTable;
